import {
	Check,
	ChevronDown,
	Columns3,
	FolderGit2,
	Inbox,
	LayoutList,
	Rows3,
	Ticket,
} from "lucide-react";
import React from "react";
import { VIEW_MODE } from "../constants";
import { DropdownMenu } from "./DropdownMenu";

const VIEW_ITEMS = [
	{
		mode: VIEW_MODE.STANDARD,
		label: "Standard",
		description: "Classic list of merge requests",
		icon: LayoutList,
	},
	{
		mode: VIEW_MODE.SWIMLANE,
		label: "Swimlane",
		description: "Drag MRs between lanes",
		icon: Rows3,
	},
	{
		mode: VIEW_MODE.REVIEW_BOARD,
		label: "Review Board",
		description: "Kanban-style review columns",
		icon: Columns3,
	},
	{
		mode: VIEW_MODE.REPO,
		label: "By Repository",
		description: "Open MRs grouped by repo",
		icon: FolderGit2,
	},
	{
		mode: VIEW_MODE.JIRA,
		label: "By Jira",
		description: "Open MRs grouped by ticket",
		icon: Ticket,
	},
	{
		mode: VIEW_MODE.COMMENT_ATTENTION,
		label: "Needs Response",
		description: "Comments awaiting your reply",
		icon: Inbox,
	},
];

// Actionable count beside a view label (e.g. unanswered threads for Needs
// Response); orange matches the response inbox's own accent.
const ViewCount = ({ count, label }) => (
	<span
		className="ml-auto rounded-full bg-orange-500/10 px-1.5 text-xs font-medium leading-5 tabular-nums text-[var(--status-attention-text)] ring-1 ring-orange-500/20"
		aria-label={`${count} ${label}`}
	>
		{count}
	</span>
);

export const ViewMenu = ({ viewMode, onSetViewMode, counts = {} }) => {
	const active = VIEW_ITEMS.find((item) => item.mode === viewMode);
	const ActiveIcon = active?.icon || LayoutList;

	return (
		<DropdownMenu
			ariaLabel={`Change view (current: ${active?.label || "Standard"})`}
			title="Change view"
			align="left"
			panelClassName="w-64"
			buttonClassName="group flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-[var(--color-text-primary)] hover:bg-[var(--surface-elevated-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 active:scale-[0.96] transition-[background-color,color,transform]"
			trigger={
				<>
					<ActiveIcon className="h-3.5 w-3.5 shrink-0 text-[var(--surface-elevated-muted)]" />
					<span className="hidden items-baseline gap-1.5 sm:inline-flex">
						<span className="text-xs font-normal text-[var(--surface-elevated-muted)]">
							View
						</span>
						<span>{active?.label || "Standard"}</span>
					</span>
					<ChevronDown className="hidden h-3.5 w-3.5 shrink-0 text-[var(--surface-elevated-muted)] sm:inline" />
				</>
			}
		>
			{({ close }) => (
				<>
					<div className="px-2 pb-1 pt-1.5 text-xs font-semibold text-[var(--surface-elevated-muted)]">
						View
					</div>
					{VIEW_ITEMS.map(({ mode, label, description, icon: Icon }) => {
						const selected = viewMode === mode;
						return (
							<button
								key={mode}
								type="button"
								role="menuitemradio"
								aria-checked={selected}
								onClick={() => {
									onSetViewMode(mode);
									close();
								}}
								className={`flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left active:scale-[0.98] transition-[background-color,transform] ${
									selected
										? "bg-[var(--surface-elevated-hover)]"
										: "hover:bg-[var(--surface-elevated-hover)]"
								}`}
							>
								<Icon
									className={`mt-0.5 h-4 w-4 shrink-0 ${
										selected
											? "text-[var(--color-accent)]"
											: "text-[var(--surface-elevated-muted)]"
									}`}
								/>
								<span className="min-w-0 flex-1">
									<span className="flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)]">
										{label}
										{selected && (
											<Check className="h-3.5 w-3.5 shrink-0 text-[var(--color-accent)]" />
										)}
										{counts[mode] > 0 && (
											<ViewCount
												count={counts[mode]}
												label={
													counts[mode] === 1
														? "pending response"
														: "pending responses"
												}
											/>
										)}
									</span>
									<span className="mt-0.5 block text-xs text-[var(--surface-elevated-muted)]">
										{description}
									</span>
								</span>
							</button>
						);
					})}
				</>
			)}
		</DropdownMenu>
	);
};
